import { documentStore, uiStore } from "@/store/store";
import { MaybeExportedTree, Tree, TreeNode } from "@/types/tree";
import { GlobalBlock } from "@/types/global-blocks";
import defensiveCopy from "@/util/defensive-copy";
import {
  deleteNode,
  findNode,
  insertNewNodeAt,
  ROOT_ELEMENT_ID
} from "@/lib/tree/tree";
import { isNodeADescendantOf } from "@/lib/tree/nesting";

// Insert external nodes from the global block into the current tree.
export function copyBlockNodesIntoInlineEditingElement(
  block: GlobalBlock,
  copyIntoElementId: TreeNode["id"]
) {
  const nodes = formatTreeNodesForInlineEditing(block.id, block?.tree);
  const parentNode = findNode(documentStore.document.tree, copyIntoElementId);

  if (parentNode?.children.length) {
    console.log(
      "The parent node already has children, this should not happen, unless it's an undo transaction.",
      parentNode.children
    );
    return;
  }

  nodes.forEach((node, index) =>
    insertNewNodeAt(documentStore.document.tree, node, copyIntoElementId, index)
  );

  return nodes;
}

export function getNodesFromInlineEditingElement(element: TreeNode) {
  return defensiveCopy(element.children).map(node => {
    return {
      ...removePostIdFromNodeId(node),
      _parentId: ROOT_ELEMENT_ID
    };
  });
}

export function deleteChildren(element: TreeNode) {
  const existingNodes = element.children.map(child => child.id);

  existingNodes.forEach(child =>
    deleteNode(documentStore.document.tree, child)
  );
}

export function removePostIdFromNodeId(node: TreeNode): TreeNode {
  return {
    ...node,
    id: typeof node.id === "string" ? Number(node.id.split("-")[1]) : node.id,
    children: node.children
      ? node.children.map(child => removePostIdFromNodeId(child))
      : node.children
  };
}

export function formatTreeNodesForInlineEditing(
  postId: TreeNode["id"],
  tree?: MaybeExportedTree | null
): TreeNode[] {
  if (!tree || tree.root.children === undefined) return [];
  return tree.root.children.map(child => addPostIdToNodeId(postId, child));
}

// To avoid ID collisions when inserting nodes from a global block into the current tree,
// we prefix each node ID with the post ID. This preserves existing IDs (so references don’t break)
// while ensuring uniqueness across posts.
export function addPostIdToNodeId(
  postId: TreeNode["id"],
  node: TreeNode
): TreeNode {
  return {
    ...node,
    id: `${postId}-${node.id}`,
    children: node.children
      ? node.children.map(child => addPostIdToNodeId(postId, child))
      : node.children
  };
}

export function isNodeDisallowedInsideWhenInlineEditingIsEnabled(
  tree: Tree,
  node: TreeNode
) {
  const elementId = uiStore.inlineEditingContext?.elementId;

  // Bail out if we're not editing an element
  if (!elementId) return false;

  // Making an assumption here
  const inlineNodeBeingEdited = findNode(tree, elementId)!;

  // If the node is a descendant of the inline node being edited,
  // it's allowed to move around. Otherwise, you can't drag it outside the inline node.
  const isAllowed =
    isNodeADescendantOf(tree, node, inlineNodeBeingEdited) ||
    node.id === elementId;

  return !isAllowed;
}
